import React from "react";
import { FiClock, FiTrash2 } from "react-icons/fi";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import GeneralInfoSection from "./GeneralInfoSection";
import PropertyDetailsSection from "./PropertyDetailsSection";
import AnalysisResultSection from "./AnalysisResultSection";
import ErrorSection from "./ErrorSection";

/**
 * Component chi tiết yêu cầu phân tích (Analysis Detail Modal)
 * Hiển thị thông tin có cấu trúc tốt hơn
 */
export const AnalysisDetailModal = ({
  analysis,
  statusInfo,
  formatDate,
  onClose,
  onDelete,
  getAllS3Files,
  isMarkdownFile,
  viewMarkdownFile,
  downloadFile,
}) => {
  const allS3Files = getAllS3Files(analysis);
  const analysisTitle =
    analysis.result?.updated_page_data?.current_task_title ||
    analysis.result?.updated_page_data?.page_title ||
    "Chi tiết phân tích giá";

  // Helper function: Xác định loại định giá (bán hay thuê)
  const getPriceType = (item) => {
    const title =
      item.result?.updated_page_data?.current_task_title || item.message || "";
    if (title.toLowerCase().includes("thuê")) {
      return {
        label: "Thuê BDS",
        bgColor: "bg-purple-100",
        textColor: "text-purple-700",
        borderColor: "border-purple-300",
        icon: "🏠",
      };
    }
    return {
      label: "Bán BDS",
      bgColor: "bg-blue-100",
      textColor: "text-blue-700",
      borderColor: "border-blue-300",
      icon: "💰",
    };
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
              Chi tiết phân tích
            </p>
            <h3 className="text-lg sm:text-2xl font-bold text-gray-900">
              {analysisTitle}
            </h3>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs sm:text-sm text-gray-500">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.bgColor} ${statusInfo.color}`}
              >
                {statusInfo.text}
              </span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  getPriceType(analysis).bgColor
                } ${getPriceType(analysis).textColor} border ${
                  getPriceType(analysis).borderColor
                }`}
              >
                {getPriceType(analysis).label}
              </span>
              <div className="flex items-center">
                <FiClock className="mr-1" />
                {formatDate(analysis.completed_at || analysis.created_at)}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-2xl text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        {/* Content - Improved Sections */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 admin-scrollbar-thin">
          {/* 1. General Info Section */}
          <GeneralInfoSection analysis={analysis} formatDate={formatDate} />

          {/* 2. Property Details Section */}
          <PropertyDetailsSection analysis={analysis} />

          {/* 3. Analysis Result Section */}
          <AnalysisResultSection analysis={analysis} />

          {/* 4. User Input Section */}
          {analysis.result?.chat_response?.user_message && (
            <section className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-6 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-gray-900 font-semibold">
                <span className="text-lg">📥</span>
                Thông Tin Yêu Cầu Từ Người Dùng
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-3 text-sm text-gray-700 whitespace-pre-wrap admin-scrollbar-thin max-h-64 overflow-y-auto">
                {analysis.result.chat_response.user_message}
              </div>
            </section>
          )}

          {/* 5. Error Section */}
          {analysis.result?.error && (
            <ErrorSection error={analysis.result.error} />
          )}

          {/* 6. Raw JSON Section */}
          <section className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-6 shadow-sm">
            <details>
              <summary className="cursor-pointer text-base font-semibold text-gray-900 hover:text-gray-700 mb-3">
                🔧 Dữ Liệu JSON (Developer)
              </summary>
              <pre className="payment-json admin-scrollbar-thin max-h-64 text-xs">
                {JSON.stringify(analysis, null, 2)}
              </pre>
            </details>
          </section>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500">
            ID bản ghi: <span className="font-mono">{analysis.id}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (
                  window.confirm(
                    "Bạn có chắc chắn muốn xóa bản ghi phân tích này?"
                  )
                ) {
                  onDelete(analysis.id);
                  onClose();
                }
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-red-50 text-red-600 font-semibold hover:bg-red-100"
            >
              <FiTrash2 />
              Xóa bản ghi
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-2xl bg-gray-900 text-white font-semibold hover:bg-gray-800"
            >
              Đóng
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisDetailModal;
